import { applyMyersDiff, applyMyersDiffString } from './myersDiff.js';

/**
 * Default strategy for objects - property by property merge
 * Assumes deepEqual checks already performed by merge function
 * @param local
 * @param master
 * @param ancestor
 * @param mergeNested
 */
export function objectStrategy(local, master, ancestor, mergeNested) {
  if (ancestor === undefined || ancestor === null) ancestor = {}; // Both created new objects
  if (master === undefined) return undefined; // Master deletion wins
  if (local === undefined || local === null) return master; // Conflicting local deletion - master wins

  // Merge property by property
  const allKeys = new Set([
    ...Object.keys(local),
    ...Object.keys(master),
    ...Object.keys(ancestor),
  ]);

  const result = {};
  for (const key of allKeys) {
    const merged = mergeNested(local[key], master[key], ancestor[key], key);
    if (merged !== undefined) {
      result[key] = merged;
    }
  }

  return result;
}

/**
 * Type-based strategy - delegates to objectStrategy or masterWinsStrategy based on type
 * @param local
 * @param master
 * @param ancestor
 * @param mergeNested
 */
export function typeBasedStrategy(local, master, ancestor, mergeNested) {
  if (typeof master === 'object' && master !== null && !Array.isArray(master)) {
    return objectStrategy(local, master, ancestor, mergeNested);
  }
  return masterWinsStrategy(local, master, ancestor, mergeNested);
}

/**
 * Master wins strategy - always prefers master when there's a conflict
 * Assumes deepEqual checks already performed by merge function
 *
 * @param {*} local - Local value
 * @param {*} master - Master value
 * @param {*} ancestor - Common ancestor
 * @param {Function} mergeNested - Recursively merge nested values
 * @returns {*} - Merged value
 */
export function masterWinsStrategy(local, master, ancestor, mergeNested) {
  return master;
}

/**
 * Local wins strategy - prefers local when there's a conflict
 * Assumes deepEqual checks already performed by merge function
 *
 * @param {*} local - Local value
 * @param {*} master - Master value
 * @param {*} ancestor - Common ancestor
 * @param {Function} mergeNested - Recursively merge nested values
 * @returns {*} - Merged value
 */
export function localWinsStrategy(local, master, ancestor, mergeNested) {
  return mergeNested(master, local, ancestor, '');
}

/**
 * Myers' Diff Strategy for arrays - simple 3-way merge
 * Preserves non-conflicting changes from both branches
 *
 * @param {Array} local - Local array
 * @param {Array} master - Master array
 * @param {Array} ancestor - Ancestor array
 * @param {Function} mergeNested - Recursively merge nested values
 * @returns {Array} - Merged array
 */
export function myersDiffStrategy(local, master, ancestor, mergeNested) {
  if (!Array.isArray(ancestor)) ancestor = [];
  if (!Array.isArray(master)) return master;
  if (!Array.isArray(local)) return master;

  // If both created from nothing, master wins
  if (ancestor.length === 0) return master;

  return applyMyersDiff(local, master, ancestor);
}

/**
 * Master Order Wins Strategy Factory
 * For arrays where order matters. Master's order wins when both changed.
 *
 * @param {string|Function} keyField - Field name or function to extract key from objects
 * @param {object} options - Options { unstrict: boolean }
 * @returns {Function} - Strategy function
 */
export function masterOrderWinsStrategy(keyField = null, options = {}) {
  const { unstrict = false } = options;

  return function (local, master, ancestor, mergeNested) {
    if (!Array.isArray(ancestor)) ancestor = [];
    if (!Array.isArray(master)) return master;
    if (!Array.isArray(local)) return master;

    // Extract keys, replacing undefined with a temporary unique id
    const getKey = item =>
      (keyField
        ? typeof keyField === 'function'
          ? keyField(item)
          : item?.[keyField]
        : item) ?? crypto.randomUUID();

    const ancestorKeys = ancestor.map(getKey);
    const ancestorKeySet = new Set(ancestorKeys);
    const localKeys = local.map(getKey);
    const masterKeys = master.map(getKey);

    // Master order changed if length or order differs
    const masterOrderChanged =
      masterKeys.length !== ancestorKeys.length ||
      masterKeys.some((k, i) => k !== ancestorKeys[i]);

    // Build lookup maps
    const localMap = new Map(local.map((item, i) => [localKeys[i], item]));
    const masterMap = new Map(master.map((item, i) => [masterKeys[i], item]));
    const ancestorMap = new Map(
      ancestor.map((item, i) => [ancestorKeys[i], item])
    );

    // Use master order if changed, else local order
    if (!masterOrderChanged) {
      return localKeys.map(key =>
        mergeNested(
          localMap.get(key),
          masterMap.get(key),
          ancestorMap.get(key),
          ''
        )
      );
    }

    // Master order changed - use master order
    if (!unstrict) {
      return masterKeys.map(key =>
        localMap.has(key)
          ? mergeNested(
              localMap.get(key),
              masterMap.get(key),
              ancestorMap.get(key),
              ''
            )
          : masterMap.get(key)
      );
    }

    // Unstrict mode: merge concurrent insertions at same position
    const result = [];
    const usedLocalKeys = new Set();

    // Group insertions by their anchor point (last ancestor key before them)
    // This helps match insertions at the same position
    const getAnchorKey = (keys, index, ancestorSet) => {
      // Look backwards for the last key that exists in ancestor
      for (let i = index - 1; i >= 0; i--) {
        if (ancestorSet.has(keys[i])) return keys[i];
      }
      return null; // Insertion at the beginning
    };

    // Build map of insertion sequences by anchor point for local
    const localInsertionsByAnchor = new Map();
    for (let li = 0; li < localKeys.length; li++) {
      const localKey = localKeys[li];
      if (ancestorKeySet.has(localKey)) continue; // Not an insertion

      const anchor = getAnchorKey(localKeys, li, ancestorKeySet);
      if (!localInsertionsByAnchor.has(anchor)) {
        localInsertionsByAnchor.set(anchor, []);
      }
      localInsertionsByAnchor.get(anchor).push(localKey);
    }

    for (let mi = 0; mi < masterKeys.length; mi++) {
      const masterKey = masterKeys[mi];
      const masterIsInsert = !ancestorKeySet.has(masterKey);

      // Find corresponding position in local
      let localInsertAtSamePos = null;
      if (masterIsInsert) {
        // Find the anchor point for this master insertion
        const anchor = getAnchorKey(masterKeys, mi, ancestorKeySet);

        // Find how many insertions we've already used at this anchor
        const insertionsAtAnchor = localInsertionsByAnchor.get(anchor) || [];
        const masterInsertionsBeforeThis = masterKeys
          .slice(0, mi)
          .filter(
            k =>
              !ancestorKeySet.has(k) &&
              getAnchorKey(
                masterKeys,
                masterKeys.indexOf(k),
                ancestorKeySet
              ) === anchor
          ).length;

        // Match by position in insertion sequence at this anchor
        if (masterInsertionsBeforeThis < insertionsAtAnchor.length) {
          const candidateKey = insertionsAtAnchor[masterInsertionsBeforeThis];
          if (!usedLocalKeys.has(candidateKey)) {
            localInsertAtSamePos = candidateKey;
            usedLocalKeys.add(candidateKey);
          }
        }
      }

      // Merge the items
      if (localInsertAtSamePos) {
        // Both inserted at same position - merge them
        const merged = mergeNested(
          localMap.get(localInsertAtSamePos),
          masterMap.get(masterKey),
          undefined,
          ''
        );
        result.push(merged);
      } else if (localMap.has(masterKey)) {
        // Regular merge
        result.push(
          mergeNested(
            localMap.get(masterKey),
            masterMap.get(masterKey),
            ancestorMap.get(masterKey),
            ''
          )
        );
      } else {
        // Local doesn't have this key — master wins (no real conflict)
        result.push(masterMap.get(masterKey));
      }
    }

    return result;
  };
}

/**
 * Set Merge Strategy Factory
 * For arrays with unique values where order does not matter
 *
 * @param {string|Function} keyField - Field name or function to extract key from objects
 * @returns {Function} - Strategy function
 */
export function setMergeStrategy(keyField = null) {
  return function (local, master, ancestor, mergeNested) {
    if (!Array.isArray(ancestor)) ancestor = [];
    if (!Array.isArray(master)) return master;
    if (!Array.isArray(local)) return master;

    // Extract keys
    const getKey = keyField
      ? typeof keyField === 'function'
        ? keyField
        : item => item?.[keyField]
      : item => item;

    // Build lookup maps
    const localMap = new Map(local.map(item => [getKey(item), item]));
    const masterMap = new Map(master.map(item => [getKey(item), item]));
    const ancestorMap = new Map(ancestor.map(item => [getKey(item), item]));

    const ancestorKeys = new Set(ancestorMap.keys());
    const localKeys = new Set(localMap.keys());
    const masterKeys = new Set(masterMap.keys());

    // Collect all keys from local and master
    const allKeys = new Set([...localKeys, ...masterKeys]);

    // Build result with conflict resolution
    const result = [];
    for (const key of allKeys) {
      const inAncestor = ancestorKeys.has(key);
      const inLocal = localKeys.has(key);
      const inMaster = masterKeys.has(key);

      // Master deleted → always delete (master wins)
      if (inAncestor && !inMaster) continue;

      // Merge: handles update vs delete conflicts
      // - Local deleted + master unchanged → returns undefined (delete)
      // - Local deleted + master updated → returns master (update wins)
      // - Both added → merge them
      // - Both kept → merge them
      const merged = mergeNested(
        localMap.get(key),
        masterMap.get(key),
        ancestorMap.get(key),
        '*'
      );

      // Skip undefined results (deletions)
      if (merged !== undefined) {
        result.push(merged);
      }
    }

    return result;
  };
}

/**
 * String Merge Strategy using Myers' Diff
 * For string values where you want line-based or character-based merging
 *
 * @param {object} options - Options { charBased: boolean }
 * @returns {Function} - Strategy function
 */
export function stringMergeStrategy(options = {}) {
  const { charBased = false } = options;

  return function (local, master, ancestor, mergeNested) {
    if (typeof ancestor !== 'string') ancestor = '';
    if (typeof master !== 'string') return master;
    if (typeof local !== 'string') return master;

    // If both created from nothing, master wins
    if (ancestor === '') return master;

    return applyMyersDiffString(local, master, ancestor, charBased);
  };
}
